import Class from "../Class";
import Mapping from "../Mapping";
import EventSource from "../EventSource";
import setImmediate from "../time/setImmediate";
import combiners from "../combiners";

const KeyComparator = new Class({
    initialize: function(combiner) {
        this._combiner = combiner;
    },

    cmp: function(left, right) {
        return this._combiner.cmpKeys(left, right);
    }
});

export default new Class({
    Implements: EventSource,

    initialize: function(_combiner, _autoCommit) {
        const combiner = _combiner || new combiners.Group();
        const autoCommit = _autoCommit === void 0 ? true : Boolean(_autoCommit);

        this._combiner = combiner;
        this._nextId = 0;

        this._rows = new Mapping(new KeyComparator(combiner));
        this._changed = [];
        this._changesAnnounced = false;

        this._autoCommit = autoCommit;
        this._autoCommitScheduled = false;
        this._autoCommitCallback = function() {
            this._autoCommitScheduled = false;

            if (this.hasChanges()) {
                this.commit();
            }
        }.bind(this);
    },

    hasChanges: function() {
        return this._changesAnnounced;
    },

    _announceChanges: function() {
        if (this._changesAnnounced || this._changed.length === 0) {
            return;
        }

        this._changesAnnounced = true;
        this.trigger("hasChanges");

        if (this._autoCommit && !this._autoCommitScheduled) {
            this._autoCommitScheduled = true;
            setImmediate(this._autoCommitCallback);
        }
    },

    commit: function() {
        this._changesAnnounced = false;

        const combiner = this._combiner;
        const rows = this._rows;
        const changed = this._changed;
        this._changed = [];

        for (let i = 0, len = changed.length; i < len; i++) {
            const obj = changed[i];
            obj.changed = false;

            if (obj.id === null && obj.count > 0) {
                obj.id = this._nextId++;
                obj.snapshot = combiner.snapshot(obj.combined);

                this.trigger("rowAdded", {
                    "id": obj.id,
                    "key": obj.key,
                    "snapshot": obj.snapshot
                });
            } else if (obj.id !== null && obj.count > 0) {
                const snapshot = obj.snapshot;
                obj.snapshot = combiner.snapshot(obj.combined);

                this.trigger("rowUpdated", {
                    "id": obj.id,
                    "key": obj.key,
                    "snapshot": snapshot
                }, {
                    "id": obj.id,
                    "key": obj.key,
                    "snapshot": obj.snapshot
                });
            } else if (obj.id !== null && obj.count <= 0) {
                rows.pop(obj.key);

                this.trigger("rowRemoved", {
                    "id": obj.id,
                    "key": obj.key,
                    "snapshot": obj.snapshot
                });
            }
        }
    },

    insert: function(item) {
        const combiner = this._combiner;
        const changed = this._changed;
        const rows = this._rows;

        const mapped = combiner.map(item);
        for (let i = 0, len = mapped.length; i < len; i++) {
            const key = mapped[i];
            let obj = rows.get(key);
            if (obj === null) {
                obj = {
                    "id": null,
                    "key": key,
                    "count": 1,
                    "changed": false,
                    "combined": combiner.init(key),
                    "snapshot": null
                };
                rows.set(key, obj);
            } else if (obj.count > 0) {
                obj.count += 1;
                obj.combined = combiner.insert(key, obj.combined);
            } else {
                obj.count = 1;
                obj.combined = combiner.init(key);
            }

            if (!obj.changed) {
                obj.changed = true;
                changed.push(obj);
            }
        }

        this._announceChanges();
    },

    remove: function(item) {
        const combiner = this._combiner;
        const changed = this._changed;
        const rows = this._rows;

        const mapped = combiner.map(item);
        for (let i = 0, len = mapped.length; i < len; i++) {
            const key = mapped[i];

            const obj = rows.get(key);
            if (obj === null) {
                continue;
            }

            if (obj.count > 1) {
                obj.count -= 1;
                obj.combined = combiner.remove(key, obj.combined);
            } else {
                obj.count = 0;
                obj.combined = null;
            }

            if (!obj.changed) {
                obj.changed = true;
                changed.push(obj);
            }
        }

        this._announceChanges();
    },

    clear: function() {
        const changed = this._changed;

        this._rows.forEach(function(obj) {
            obj.count = 0;
            obj.combined = null;

            if (!obj.changed) {
                obj.changed = true;
                changed.push(obj);
            }
        });
        this._announceChanges();
    },

    columns: function() {
        return this._combiner.columns.apply(this._combiner, arguments);
    },

    header: function(column) {
        return this._combiner.header(column);
    },

    forEachRow: function(func, ctx) {
        this._rows.forEach(function(obj) {
            if (obj.id === null) {
                return;
            }

            func.call(ctx, {
                "id": obj.id,
                "key": obj.key,
                "snapshot": obj.snapshot
            });
        });
    },

    renderer: function(type, column) {
        return this._combiner.renderer(type, column, function(item) {
            return item.snapshot;
        });
    },

    cmpColumns: function(left, right) {
        return this._combiner.cmpColumns(left, right);
    },

    cmpRows: function(left, right) {
        return left.id - right.id;
    },

    cmpValues: function(left, right, column) {
        return this._combiner.cmpValues(left.snapshot, right.snapshot, column);
    },

    filter: function(row) {
        const key = row.key;
        const combiner = this._combiner;

        return function(item) {
            const mapped = combiner.map(item);
            for (let i = 0, len = mapped.length; i < len; i++) {
                const result = combiner.cmpKeys(mapped[i], key);
                if (result === 0) {
                    return true;
                }
            }
            return false;
        };
    }
});



// WEBPACK FOOTER //
// ./src/abusesa-js/ui/CombinerModel.js